// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext } from '../app';
import type { Substance } from '../types';
import { el, clear } from '../dom';
import { formatMass, formatYear } from '../format';
import { substanceCategoryColor, stateColor } from '../colors';

export function renderSubstances(root: HTMLElement, ctx: AppContext) {
  root.appendChild(el('h1', { class: 'section-title' }, 'Substances'));
  root.appendChild(el('p', { class: 'section-sub' }, [
    'The NPI tracks ',
    el('span', { class: 'glossary-link', 'data-term': 'substance', tabindex: '0' }, '93 chemicals and pollutants'),
    '. Click any card to see the top emitters, by-state breakdown, and 25-year trend.'
  ]));

  const categoryOrder = [
    'Criteria air pollutants', 'Particulates', 'Heavy metals', 'Organics & VOCs',
    'Acid gases & inorganics', 'Persistent organic pollutants', 'Other inorganics', 'Other'
  ];
  const grouped = new Map<string, Substance[]>();
  for (const s of ctx.data.substances) {
    let arr = grouped.get(s.category);
    if (!arr) { arr = []; grouped.set(s.category, arr); }
    arr.push(s);
  }
  for (const arr of grouped.values()) arr.sort((a, b) => b.total - a.total);

  // Selected substance detail container
  const detail = el('div', { class: 'mt-lg' });

  // Category grid
  for (const cat of categoryOrder) {
    const arr = grouped.get(cat);
    if (!arr || arr.length === 0) continue;
    const wrap = el('div', { class: 'mb-lg' });
    wrap.appendChild(el('h2', { style: 'font-size: var(--font-size-lg); margin-bottom: var(--space-sm); display:flex; align-items:center; gap: var(--space-sm)' }, [
      el('span', { class: 'legend-swatch', style: `background:${substanceCategoryColor(cat)}` }),
      cat,
      el('span', { class: 'text-tertiary', style: 'font-size: var(--font-size-xs); font-weight: 400' }, ` · ${arr.length}`)
    ]));
    const grid = el('div', { style: 'display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: var(--space-sm);' });
    for (const s of arr) {
      const card = el('button', {
        class: 'card',
        style: 'text-align:left; cursor:pointer; padding: var(--space-md); transition: transform 150ms ease;',
        onclick: () => {
          showSubstance(s);
          detail.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, [
        el('div', { style: 'font-weight: 600; font-size: var(--font-size-sm); margin-bottom: 4px' }, s.name),
        el('div', { class: 'text-mono', style: 'font-size: var(--font-size-xs); color: var(--text-secondary)' }, formatMass(s.total))
      ]);
      grid.appendChild(card);
    }
    wrap.appendChild(grid);
    root.appendChild(wrap);
  }

  root.appendChild(detail);

  // Open first substance by default
  if (ctx.data.substances.length > 0) showSubstance(ctx.data.substances[0]);

  function showSubstance(s: Substance) {
    clear(detail);
    const head = el('div', { class: 'card mb-lg' }, [
      el('div', { class: 'flex-row', style: 'justify-content: space-between' }, [
        el('div', null, [
          el('h2', { style: 'font-size: var(--font-size-xl); font-weight: 700' }, s.name),
          el('div', { class: 'text-secondary', style: 'font-size: var(--font-size-sm); margin-top: 4px' }, [
            s.cas ? `CAS ${s.cas} · ` : '',
            s.category,
            s.voc ? ' · VOC' : '',
            s.first_year ? ` · Tracked since ${formatYear(s.first_year)}` : ''
          ])
        ]),
        s.fact_sheet_url ? el('a', { href: s.fact_sheet_url, target: '_blank', rel: 'noopener', class: 'btn' }, 'Fact sheet ↗') : null
      ]),
      el('div', { class: 'grid-3 mt-lg' }, [
        kpi('Lifetime total', formatMass(s.total)),
        kpi('Top state', s.top_facilities[0] ? formatMass(s.top_facilities[0].kg) : '—'),
        kpi('Top emitter', s.top_facilities[0] ? (ctx.facilitiesById.get(s.top_facilities[0].id)?.name ?? '—') : '—')
      ])
    ]);
    detail.appendChild(head);

    // Trend + state breakdown side by side
    const grid = el('div', { class: 'grid-2 mb-lg' });

    const trend = el('div', { class: 'card' });
    trend.appendChild(el('h3', { class: 'card-title' }, 'National trend by reporting year'));
    trend.appendChild(el('p', { class: 'card-subtitle' }, 'Total release across air, water and land each year.'));
    trend.appendChild(buildTrendChart(s, ctx.data.meta.earliest_year ?? 2000, ctx.data.meta.latest_year));
    grid.appendChild(trend);

    const states = el('div', { class: 'card' });
    states.appendChild(el('h3', { class: 'card-title' }, 'By state — lifetime total'));
    states.appendChild(el('p', { class: 'card-subtitle' }, 'Which jurisdictions account for the bulk of releases?'));
    states.appendChild(buildStateBars(s));
    grid.appendChild(states);

    detail.appendChild(grid);

    // Top emitters
    const tops = el('div', { class: 'card' });
    tops.appendChild(el('h3', { class: 'card-title' }, 'Top 25 emitters (lifetime)'));
    tops.appendChild(el('p', { class: 'card-subtitle' }, 'Click any facility to open its detail panel.'));
    const wrap = el('div', { class: 'table-wrap' });
    const max = s.top_facilities[0]?.kg ?? 1;
    const t = el('table', { class: 'data' }, [
      el('thead', null, [el('tr', null, [
        el('th', null, '#'),
        el('th', null, 'Facility'),
        el('th', null, 'State'),
        el('th', null, 'Industry'),
        el('th', null, `${s.name} (kg)`),
        el('th', null, '')
      ])]),
      el('tbody', null, s.top_facilities.slice(0, 25).map((tf, i) => {
        const f = ctx.facilitiesById.get(tf.id);
        return el('tr', { class: 'clickable', onclick: () => ctx.openFacility(tf.id) }, [
          el('td', { class: 'num' }, String(i + 1)),
          el('td', null, [
            el('div', null, f?.name ?? `Facility #${tf.id}`),
            el('div', { class: 'text-tertiary', style: 'font-size: var(--font-size-xs)' }, f?.suburb ?? '—')
          ]),
          el('td', null, [el('span', {
            class: 'pill pill-state',
            style: `background:${stateColor(f?.state ?? null)}22;color:${stateColor(f?.state ?? null)};border-color:${stateColor(f?.state ?? null)}40`
          }, f?.state ?? '—')]),
          el('td', { class: 'text-secondary', style: 'font-size: var(--font-size-xs)' }, f?.anzsic_class_name ?? '—'),
          el('td', { class: 'num' }, formatMass(tf.kg)),
          el('td', null, [bar(tf.kg / max, substanceCategoryColor(s.category))])
        ]);
      }))
    ]);
    wrap.appendChild(t);
    tops.appendChild(wrap);
    detail.appendChild(tops);
  }
}

function kpi(label: string, value: string): HTMLElement {
  return el('div', null, [
    el('div', { class: 'kpi-label' }, label),
    el('div', { style: 'font-family: var(--font-mono); font-size: var(--font-size-xl); font-weight: 600;' }, value)
  ]);
}

function buildTrendChart(s: Substance, fromY: number, toY: number): HTMLElement {
  const years: number[] = [];
  for (let y = fromY; y <= toY; y++) years.push(y);
  const values = years.map(y => {
    const v = s.by_year[String(y)];
    return v ? v.a + v.w + v.l : 0;
  });
  const max = Math.max(1, ...values);

  const W = 600, H = 220, padL = 40, padR = 12, padT = 12, padB = 30;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const barW = innerW / years.length;

  const ns = 'http://www.w3.org/2000/svg';
  const svgEl = document.createElementNS(ns, 'svg');
  svgEl.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svgEl.setAttribute('class', 'chart');
  svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  // Y-axis gridlines
  for (let i = 0; i <= 4; i++) {
    const t = i / 4;
    const y = padT + innerH * (1 - t);
    const line = document.createElementNS(ns, 'line');
    line.setAttribute('x1', String(padL));
    line.setAttribute('x2', String(W - padR));
    line.setAttribute('y1', String(y));
    line.setAttribute('y2', String(y));
    line.setAttribute('stroke', 'var(--border-subtle)');
    line.setAttribute('stroke-dasharray', t === 0 ? '0' : '2,3');
    svgEl.appendChild(line);
    const label = document.createElementNS(ns, 'text');
    label.setAttribute('x', String(padL - 4));
    label.setAttribute('y', String(y));
    label.setAttribute('text-anchor', 'end');
    label.setAttribute('dominant-baseline', 'middle');
    label.setAttribute('class', 'chart-label');
    label.setAttribute('font-size', '10');
    label.textContent = formatMass(max * t);
    svgEl.appendChild(label);
  }

  // Bars
  for (let i = 0; i < years.length; i++) {
    const h = values[i] === 0 ? 0 : (values[i] / max) * innerH;
    const x = padL + i * barW + 1;
    const y = padT + innerH - h;
    const rect = document.createElementNS(ns, 'rect');
    rect.setAttribute('x', String(x));
    rect.setAttribute('y', String(y));
    rect.setAttribute('width', String(Math.max(2, barW - 2)));
    rect.setAttribute('height', String(h));
    rect.setAttribute('fill', substanceCategoryColor(s.category));
    rect.setAttribute('rx', '1');
    rect.setAttribute('class', 'chart-bar');
    const tip = `${formatYear(years[i])} · ${formatMass(values[i])}`;
    rect.setAttribute('data-tip', tip);
    rect.setAttribute('aria-label', tip);
    svgEl.appendChild(rect);

    // Year label every 5 years
    if (years[i] % 5 === 0) {
      const text = document.createElementNS(ns, 'text');
      text.setAttribute('x', String(x + barW / 2));
      text.setAttribute('y', String(H - padB + 14));
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('class', 'chart-axis');
      text.setAttribute('font-size', '10');
      text.setAttribute('fill', 'var(--text-tertiary)');
      text.textContent = String(years[i]);
      svgEl.appendChild(text);
    }
  }

  const wrap = document.createElement('div');
  wrap.appendChild(svgEl);
  return wrap;
}

function buildStateBars(s: Substance): HTMLElement {
  const order = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
  const items = order.map(st => ({ state: st, kg: s.by_state[st] ?? 0 }));
  items.sort((a, b) => b.kg - a.kg);
  const max = items[0].kg || 1;
  return el('div', { style: 'display: flex; flex-direction: column; gap: var(--space-sm);' },
    items.map(i => el('div', { style: 'display: grid; grid-template-columns: 50px 1fr 110px; gap: var(--space-sm); align-items: center;' }, [
      el('span', {
        class: 'pill pill-state',
        style: `background:${stateColor(i.state)}22;color:${stateColor(i.state)};border-color:${stateColor(i.state)}40`
      }, i.state),
      el('div', { style: 'background: var(--bg-elevated); border-radius: 4px; height: 10px; overflow: hidden;' }, [
        el('div', { style: `background: ${stateColor(i.state)}; height: 100%; width: ${(i.kg / max) * 100}%;` })
      ]),
      el('span', { class: 'text-mono', style: 'font-size: var(--font-size-xs); text-align:right' }, formatMass(i.kg))
    ]))
  );
}

function bar(frac: number, color: string): HTMLElement {
  const w = Math.max(2, Math.min(100, frac * 100));
  return el('div', { style: 'background: var(--bg-elevated); border-radius: 4px; height: 8px; width: 100px; overflow: hidden;' }, [
    el('div', { style: `background: ${color}; height: 100%; width: ${w}%;` })
  ]);
}
